import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { useCreateTestimonial } from "../../hooks/testimonials/useCreateTestimonial";
import { useUpdateTestimonial } from "../../hooks/testimonials/useUpdateTestimonial";
import FormInput from "../inputs/FormInput";
import LanguageToggle from "../global/LanguageToggle";

const FormAddEditTestimonial = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";
    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");

    const [nameAr, setNameAr] = useState("");
    const [nameEn, setNameEn] = useState("");
    const [descriptionAr, setDescriptionAr] = useState("");
    const [descriptionEn, setDescriptionEn] = useState("");
    const [rating, setRating] = useState(5);
    const [showInHome, setShowInHome] = useState(true);
    const [sortOrder, setSortOrder] = useState(0);

    const { createItemMutate, isPending: isCreating } = useCreateTestimonial();
    const { updateItemMutate, isPending: isUpdating } = useUpdateTestimonial();

    const loadingSubmit = isCreating || isUpdating;

    useEffect(() => {
        if (isEdit && currentData) {
            setNameAr(currentData?.name?.ar || "");
            setNameEn(currentData?.name?.en || "");
            setDescriptionAr(currentData?.description?.ar || "");
            setDescriptionEn(currentData?.description?.en || "");
            setRating(currentData?.rating ?? 5);
            setShowInHome(currentData?.show_in_home ?? true);
            setSortOrder(currentData?.sort_order ?? 0);
        }
    }, [isEdit, currentData]);

    const handleSubmit = (e) => {
        e.preventDefault();

        const payload = {
            name: {
                ar: nameAr || nameEn,
                en: nameEn || nameAr,
            },
            description: {
                ar: descriptionAr || descriptionEn,
                en: descriptionEn || descriptionAr,
            },
            rating: Number(rating),
            show_in_home: showInHome ? 1 : 0,
            sort_order: Number(sortOrder),
        };

        if (isEdit) {
            updateItemMutate({ id: currentData.id, data: payload });
        } else {
            createItemMutate(payload);
        }

    };

    const rateOptions = [
        { id: 1, name: { ar: "1 - سيء", en: "1 - Poor" } },
        { id: 2, name: { ar: "2 - مقبول", en: "2 - Fair" } },
        { id: 3, name: { ar: "3 - جيد", en: "3 - Good" } },
        { id: 4, name: { ar: "4 - جيد جداً", en: "4 - Very Good" } },
        { id: 5, name: { ar: "5 - ممتاز", en: "5 - Excellent" } },
    ];

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Typography variant="h6">
                        {isEdit ? (isArabic ? "تعديل الشهادة" : "Edit Testimonial") : (isArabic ? "إضافة شهادة جديدة" : "Add New Testimonial")}
                    </Typography>
                    <LanguageToggle activeLang={activeLang} onChange={(lng) => setActiveLang(lng)} showLabel={true} />
                </Box>

                <Grid container spacing={3}>
                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="اسم العميل (عربي)"
                                placeholder="أدخل اسم العميل بالعربي"
                                value={nameAr}
                                onChange={(e) => setNameAr(e.target.value)}
                                required
                                fullWidth
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Client Name (English)"
                                placeholder="Enter client name in English"
                                value={nameEn}
                                onChange={(e) => setNameEn(e.target.value)}
                                required
                                fullWidth
                            />
                        </Grid>
                    )}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            type="number"
                            label={isArabic ? "ترتيب العرض" : "Sort Order"}
                            placeholder="0"
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                            fullWidth
                        />
                    </Grid>


                    <Grid item xs={12} md={6}>
                        <FormInput
                            type="select"
                            label={isArabic ? "التقييم" : "Rating"}
                            value={rating}
                            onChange={(e) => setRating(Number(e.target.value))}
                            options={rateOptions}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormInput
                            type="switch"
                            label={isArabic ? "عرض في الرئيسية" : "Show in Home"}
                            name="show_in_home"
                            checked={showInHome}
                            onChange={(e) => setShowInHome(e.target.checked)}
                        />
                    </Grid>
                    {activeLang === "ar" && (
                        <Grid item xs={12}>
                            <FormInput
                                type="textarea"
                                label="الوصف (عربي)"
                                placeholder="أدخل الوصف بالعربي"
                                value={descriptionAr}
                                onChange={(e) => setDescriptionAr(e.target.value)}
                                multiline
                                rows={4}
                                fullWidth
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12}>
                            <FormInput
                                type="textarea"
                                label="Description (English)"
                                placeholder="Enter description in English"
                                value={descriptionEn}
                                onChange={(e) => setDescriptionEn(e.target.value)}
                                multiline
                                rows={4}
                                fullWidth
                            />
                        </Grid>
                    )}


                </Grid>

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loadingSubmit}
                        sx={{ minWidth: 120 }}
                    >
                        {loadingSubmit ? (isArabic ? "جاري الحفظ..." : "Saving...") : (isArabic ? "حفظ" : "Save")}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default FormAddEditTestimonial;
